import { Alert, Button, Heading, Page, Text, usePlatformTheme } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { activePlanMeetsOrExceeds, getActiveBillingPlanKey } from '../../../constants/features';
import { INTERCOM_TRIGGER } from '../../../constants/intercom';
import { useEnv } from '../../../context/env-context';
import { withPageProviders } from '../../../context/page-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { AiAssistantCard } from '../components/ai-assistant-card';
import { FeatureAccordion, type FeatureAccordionItem } from '../components/feature-accordion';
import { FeatureCategoryCards } from '../components/feature-category-cards';
import {
  FEATURE_CATEGORIES,
  FEATURE_CATEGORY_BY_ID,
  FEATURES_CATALOG,
  type FeatureCategoryId,
} from '../constants/features-catalog';

const BASE = 'features:explore.pages.features_page' satisfies I18nPrefix;

export const FeaturesPage = withPageProviders(FeaturesContent);

function FeaturesContent() {
  const env = useEnv();
  const theme = usePlatformTheme();
  const activeBillingPlanKey = getActiveBillingPlanKey(env.billing.plan_key);
  const platform = env.connected_platform ?? env.platform;

  const [selectedCategory, setSelectedCategory] = useState<FeatureCategoryId>('basics');
  const selected = FEATURE_CATEGORY_BY_ID[selectedCategory];

  const features: FeatureAccordionItem[] = FEATURES_CATALOG.filter(
    (feature) => feature.category === selectedCategory,
  )
    .filter(
      (feature) =>
        !feature.availablePlatforms ||
        (platform !== undefined && feature.availablePlatforms.includes(platform)),
    )
    // Remove direct links into the dashboard from Shopify on primary workflows
    .filter((feature) => !(theme === 'polaris' && feature.id === 'product-ordering-rules'))
    .map((feature) => ({
      ...feature,
      locked: feature.minPlan
        ? !activeBillingPlanKey || !activePlanMeetsOrExceeds(activeBillingPlanKey, feature.minPlan)
        : false,
    }));

  return (
    <Page title={t(`${BASE}.title`)} description={t(`${BASE}.subtitle`)}>
      {!activeBillingPlanKey && (
        <Alert headingLevel="h2" variant="warning" title={t(`${BASE}.no_billing_alert.title`)}>
          <div className="flex flex-col gap-2">
            {t(`${BASE}.no_billing_alert.message`)}
            <Button className={INTERCOM_TRIGGER.contact}>{t('buttons:speak_to_team')}</Button>
          </div>
        </Alert>
      )}

      <FeatureCategoryCards
        categories={FEATURE_CATEGORIES}
        selectedId={selectedCategory}
        onSelect={setSelectedCategory}
      />

      <AiAssistantCard />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Heading level="h2" style="lg">
            {t(`${BASE}.categories.${selected.id}.name`)}
          </Heading>
          <Text variant="secondary">{t(`${BASE}.categories.${selected.id}.description`)}</Text>
        </div>
        <FeatureAccordion key={selected.id} features={features} />
      </div>
    </Page>
  );
}
